import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Bell, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { haptic } from '../utils/haptic';
import {
  enablePushForUser,
  getPushPermission,
  isPushSupported,
} from '../utils/pushNotifications';
import { safeGet, safeSet } from '../utils/safeStorage';

/**
 * Prompt de "ativar notificações" no momento certo: APÓS o primeiro
 * check-in bem sucedido.
 *
 * Por que esse momento:
 *  - 1º abrir: user nem sabe o que é o app → diz não → permission denied
 *    para sempre (no Chrome, virou denied; difícil voltar)
 *  - Após 1º check-in: user já viu valor, está engajado → conversão muito
 *    maior (~3-5x em estudos de growth)
 *
 * Trigger:
 *  - profile.checkinCount >= 1 (já fez pelo menos 1 check-in)
 *  - permission === 'default' (nunca foi perguntado)
 *  - isPushSupported (não desperdiça em Safari iOS sem PWA install)
 *  - Não foi dispensado antes (flag em localStorage)
 *
 * Visual: card bottom-sheet amigável, copy direta. Botão grande "Ativar"
 * + secundário "Agora não".
 */

const DISMISSED_KEY = '@sixpass:push-prompt-dismissed-at';
// Reaparece depois de 14 dias se user dispensar (não no "Ativar")
const REAPPEAR_AFTER_DAYS = 14;

export function PushPromptAfterCheckin() {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [show, setShow] = useState(false);
  const [activating, setActivating] = useState(false);

  // Checa suporte do device
  useEffect(() => {
    let mounted = true;
    void (async () => {
      const ok = await isPushSupported();
      if (mounted) setSupported(ok);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Decide se mostra. Roda quando profile/supported mudam.
  useEffect(() => {
    if (supported !== true) return;
    if (!profile || profile.role !== 'student') return;
    if ((profile.checkinCount ?? 0) < 1) return;
    if (getPushPermission() !== 'default') return;

    // Cooldown se já foi dispensado
    const dismissedAt = Number(safeGet(DISMISSED_KEY) || '0');
    if (dismissedAt) {
      const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
      if (daysSince < REAPPEAR_AFTER_DAYS) return;
    }

    // Delay pequeno pra não pular em cima do check-in (que tem confetes/toast)
    const t = window.setTimeout(() => setShow(true), 2200);
    return () => window.clearTimeout(t);
  }, [profile, supported]);

  const handleDismiss = () => {
    setShow(false);
    safeSet(DISMISSED_KEY, String(Date.now()));
  };

  const handleActivate = async () => {
    if (!profile?.uid || activating) return;
    setActivating(true);
    haptic('medium');
    try {
      await enablePushForUser(profile.uid);
      showToast('Notificações ativadas — vamos te avisar do que importa!', 'success');
      setShow(false);
    } catch (err) {
      console.error('[PushPrompt] falha ativando push', err);
      showToast(
        'Não rolou agora. Vai em Configurações pra ativar manualmente.',
        'error',
      );
      // Não dispensa permanente — user pode tentar de novo via Settings
      setShow(false);
    } finally {
      setActivating(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          className="fixed inset-x-3 bottom-3 z-[55] sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
          role="dialog"
          aria-modal="false"
          aria-labelledby="push-prompt-title"
        >
          <div className="relative rounded-3xl border border-blue-400/30 bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl shadow-black/50 backdrop-blur-md overflow-hidden">
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Fechar"
              className="absolute right-2 top-2 p-2 rounded-full text-slate-400 hover:text-slate-200 hover:bg-white/5 transition-colors"
            >
              <X size={14} aria-hidden="true" />
            </button>

            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-pink-500 shadow-lg shadow-blue-500/30">
                <Bell size={18} className="text-white" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0 pr-4">
                <h3
                  id="push-prompt-title"
                  className="text-sm font-black text-white leading-tight"
                >
                  Mantém a sequência viva!
                </h3>
                <p className="text-xs text-slate-400 leading-snug mt-1">
                  Te aviso de novos parceiros, missões e quando a streak tá
                  prestes a quebrar. Sem spam.
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={handleActivate}
                disabled={activating}
                className="flex-1 rounded-full bg-gradient-to-r from-blue-500 to-pink-500 py-2.5 text-xs font-black uppercase tracking-wide text-white shadow-md transition hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
              >
                {activating ? 'Ativando…' : 'Ativar'}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-full px-3 py-2.5 text-xs font-bold text-slate-400 hover:text-slate-200 transition-colors"
              >
                Agora não
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
